import React, { useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { Modal, SubmitButton } from 'framework/ui/ui-builder';
import { CreateForm, DEFAULT_ERROR_MESSAGE, useFormContext } from 'framework/form';
import { FamilyMember } from '../shared/types';
import FamilyMemberItem from '../shared/FamilyMemberItem';
import { useEmployeeService } from 'domain/shared/employee';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import getFamilyInfoValidationSchema from './familyInfoValidationSchema';
import { usePrepopulationContext } from 'domain/people/employee/prepopulation/PrepopulationContext';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';

interface FamilyPrepopulateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data?: { extendedInformation?: { children?: FamilyMember[]; other?: FamilyMember[] } }) => void;
}

const FamilyPrepopulateModal: React.FC<FamilyPrepopulateModalProps> = ({ isOpen, onClose, onSave }) => {
  const { data, loading } = usePrepopulationContext();
  const { addMessage } = useMessageService();
  const { updateFamilyInfo } = useEmployeeService();
  const employeeId = useEmployeeId();

  const mapPrepopulationForms = useCallback((prepopulationData: any) => {
    const familyMembers = prepopulationData?.familyMembers || prepopulationData?.info?.data?.familyMembers || [];

    return {
      request: familyMembers.map((member: any) => ({
        name: {
          firstName: member.name?.firstName || '',
          middleName: member.name?.middleName || null,
          lastName: member.name?.lastName || '',
          maidenName: member.name?.maidenName || null,
        },
        dateOfBirth: member.dateOfBirth || '',
        relation: member.relation || '',
        phoneNumber: member.phoneNumber || null,
      })),
    };
  }, []);

  const initialData = data && Object.keys(data).length > 0 ? mapPrepopulationForms(data) : { request: [] };

  const ModalContent = () => {
    const { submitForm, isSubmitEnabled, setFormData = () => {}, data: formData = {} } = useFormContext();

    useEffect(() => {
      if (data && Object.keys(data).length > 0) {
        const mapped = mapPrepopulationForms(data);

        setFormData({
          ...formData,
          request: mapped.request,
        });
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    return (
      <Modal
        isOpen={isOpen}
        onToggle={onClose}
        title="Family"
        modalSize="sm"
        buttons={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <SubmitButton onClick={submitForm} disabled={!isSubmitEnabled} />
          </Box>
        }
      >
        <Box>
          {formData.request?.map((member: FamilyMember, index: number) => (
            <Box
              key={`${member.name?.firstName || 'member'}-${member.name?.lastName || 'unknown'}-${member.relation || 'relation'}`}
              mb={2}
            >
              <FamilyMemberItem member={member} index={index} type="other" isEditMode removeFamilyMember={() => {}} />
            </Box>
          ))}
          {(!formData.request || formData.request.length === 0) && (
            <Box p={2} textAlign="center" color="text.secondary">
              No family-related information was provided by the user to prepopulate this section.
            </Box>
          )}
        </Box>
      </Modal>
    );
  };

  const handleSubmit = async (formData: any) => {
    try {
      if (employeeId) {
        const familyMembers = formData.request || [];

        const familyData = {
          extendedInformation: {
            other: familyMembers,
          },
        };

        const response = await updateFamilyInfo(employeeId, familyData);
        onSave(response);
      }

      return formData;
    } catch (error: any) {
      addMessage(new ErrorMessage(error?.message || DEFAULT_ERROR_MESSAGE));
      return null;
    }
  };

  if (loading) {
    return null;
  }

  return (
    <CreateForm
      submitFormFunc={handleSubmit}
      initialData={initialData}
      afterSubmit={{
        execute: onClose,
      }}
      getValidationSchemaFunc={getFamilyInfoValidationSchema}
    >
      <ModalContent />
    </CreateForm>
  );
};

export default FamilyPrepopulateModal;
